import React, { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { Link } from "react-router-dom";
import { FileText, FolderOpen } from "lucide-react";
import { fmtDateTime } from "../lib/datetime";

export default function Documents() {
  const [data, setData] = useState({ certs: [], reports: [] });
  const [tab, setTab] = useState("certs");

  useEffect(() => {
    Promise.all([
      api.get("/structures").then((r) => r.data),
    ]).then(async ([structs]) => {
      // Pull each structure's detail to harvest handovers + inspections (cheap for v1; collection scale low)
      const all = await Promise.all(structs.map((s) => api.get(`/structures/${s.id}`).then((r) => r.data).catch(() => null)));
      const certs = [];
      const reports = [];
      all.filter(Boolean).forEach((s) => {
        (s.handovers || []).forEach((h) => certs.push({ ...h, structure_description: s.description, area_name: s.area_name }));
        (s.inspections || []).forEach((i) => reports.push({ ...i, structure_description: s.description, area_name: s.area_name }));
      });
      certs.sort((a, b) => (b.issued_at || "").localeCompare(a.issued_at || ""));
      reports.sort((a, b) => (b.inspection_date || "").localeCompare(a.inspection_date || ""));
      setData({ certs, reports });
    });
  }, []);

  return (
    <div data-testid="documents-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 18 }}>
        <div className="mono" style={{ color: "var(--brand-accent, #F05A14)", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>COMPLIANCE</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}><FolderOpen size={26} style={{ verticalAlign: -3, marginRight: 8 }} />Documents & Certificates</h1>
        <div style={{ color: "#64748b", marginTop: 4 }}>Every PDF the system has generated — handover certificates and weekly inspection reports — in one place. Ready to share with McCain.</div>
      </header>

      <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", marginBottom: 16, gap: 4 }}>
        <button data-testid="docs-tab-certs" onClick={() => setTab("certs")} className="btn-ghost" style={{ border: "none", borderRadius: 0, borderBottom: tab === "certs" ? "3px solid var(--brand-accent, #F05A14)" : "3px solid transparent", padding: "10px 14px", fontWeight: 700, color: tab === "certs" ? "#0f172a" : "#64748b", background: "transparent" }}>Handover certificates ({data.certs.length})</button>
        <button data-testid="docs-tab-reports" onClick={() => setTab("reports")} className="btn-ghost" style={{ border: "none", borderRadius: 0, borderBottom: tab === "reports" ? "3px solid var(--brand-accent, #F05A14)" : "3px solid transparent", padding: "10px 14px", fontWeight: 700, color: tab === "reports" ? "#0f172a" : "#64748b", background: "transparent" }}>Inspection reports ({data.reports.length})</button>
      </div>

      {tab === "certs" ? (
        <div className="card" style={{ padding: 0 }} data-testid="certs-table">
          <table>
            <thead><tr><th>Cert ref</th><th>Issued</th><th>Structure</th><th>Area</th><th>McCain receipt</th><th>PDF</th></tr></thead>
            <tbody>
              {data.certs.map((h) => (
                <tr key={h.id} data-testid={`cert-row-${h.id}`}>
                  <td className="mono" style={{ fontWeight: 700 }}>{h.cert_ref || h.id.slice(0, 8)}</td>
                  <td className="mono">{fmtDateTime(h.issued_at)}</td>
                  <td><Link to={`/structures/${h.structure_id}`} style={{ color: "#0f172a", fontWeight: 600 }}>{h.structure_description}</Link></td>
                  <td>{h.area_name}</td>
                  <td>{h.mccain_receipt ? <span className="badge badge-green">{h.mccain_receipt.name}</span> : <span className="badge badge-slate">Awaiting</span>}</td>
                  <td><a href={`${API}/handovers/${h.id}/pdf`} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ padding: "4px 10px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`cert-pdf-${h.id}`}><FileText size={12} /> PDF</a></td>
                </tr>
              ))}
              {data.certs.length === 0 ? <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>No certificates yet</td></tr> : null}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }} data-testid="reports-table">
          <table>
            <thead><tr><th>Report</th><th>Inspected</th><th>Structure</th><th>Area</th><th>Result</th><th>PDF</th></tr></thead>
            <tbody>
              {data.reports.map((r) => (
                <tr key={r.id} data-testid={`report-row-${r.id}`}>
                  <td className="mono" style={{ fontWeight: 700 }}>{r.report_no || r.id.slice(0, 8)}</td>
                  <td className="mono">{fmtDateTime(r.inspection_date)}</td>
                  <td><Link to={`/structures/${r.structure_id}`} style={{ color: "#0f172a", fontWeight: 600 }}>{r.structure_description}</Link></td>
                  <td>{r.area_name}</td>
                  <td>{(r.overall_result === "Pass" || r.result === "Pass") ? <span className="badge badge-green">Pass</span> : <span className="badge badge-red">Tagged out</span>}</td>
                  <td>{r.report_no ? <a href={`${API}/inspections/${r.id}/pdf`} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ padding: "4px 10px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`report-pdf-${r.id}`}><FileText size={12} /> PDF</a> : "—"}</td>
                </tr>
              ))}
              {data.reports.length === 0 ? <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>No reports yet</td></tr> : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
